import { useBrandStrategy } from '../hooks/use-brand-strategy';
import { useGetCustomerPersonas } from '../hooks/use-update-customer-persona';
import StrategyCard from './strategy-card';

type StrategySection =
  | 'customer-personas'
  | 'value-proposition'
  | 'getting-to-know-you'
  | 'tone-of-voice'
  | 'content-plan';

interface StrategyBlocksProps {
  onSectionSelect: (section: StrategySection) => void;
  selectedSection: StrategySection;
  projectId: string;
}

const StrategyBlocks = ({
  onSectionSelect,
  selectedSection,
  projectId,
}: StrategyBlocksProps) => {
  const { data: brandStrategy, isLoading } = useBrandStrategy(projectId);
  const { data: customerPersonasData } = useGetCustomerPersonas(projectId);

  const handleCardClick = (section: StrategySection) => {
    onSectionSelect(section);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  const hasGettingToKnowYouContent = Boolean(
    brandStrategy?.purpose ||
      brandStrategy?.mission ||
      brandStrategy?.vision ||
      brandStrategy?.why_customers_choose_you ||
      brandStrategy?.why_customers_do_not_choose_you ||
      brandStrategy?.why_start_the_business ||
      brandStrategy?.unique_industry_perspective ||
      brandStrategy?.proudest_moment_in_business
  );

  const customerPersonas = customerPersonasData?.data?.customer_personas || [];

  return (
    <div>
      <div role='list' className='space-y-6'>
        <div
          onClick={() => handleCardClick('customer-personas')}
          className={`cursor-pointer transition-all duration-200 ${
            selectedSection === 'customer-personas'
              ? 'rounded-16 border border-[#0866FF] bg-white shadow-[2px_2px_8px_0px_rgba(8,102,255,0.40)]'
              : ''
          }`}
        >
          <StrategyCard title='Customer Persona(s)'>
            <span>
              {customerPersonas.length
                ? `${customerPersonas.length} customer personas defined`
                : 'Define your customer personas'}
            </span>
          </StrategyCard>
        </div>

        <div
          onClick={() => handleCardClick('value-proposition')}
          className={`cursor-pointer transition-all duration-200 ${
            selectedSection === 'value-proposition'
              ? 'rounded-16 border border-[#0866FF] bg-white shadow-[2px_2px_8px_0px_rgba(8,102,255,0.40)]'
              : ''
          }`}
        >
          <StrategyCard title='Value Proposition'>
            This section helps us better understand why a customer should choose
            your products or services over those of competitors. It highlights
            the unique benefits and value you offer, solving a specific problem
            or fulfilling a particular need for your target audience.
          </StrategyCard>
        </div>

        <div
          onClick={() => handleCardClick('getting-to-know-you')}
          className={`cursor-pointer transition-all duration-200 ${
            selectedSection === 'getting-to-know-you'
              ? 'rounded-16 border border-[#0866FF] bg-white shadow-[2px_2px_8px_0px_rgba(8,102,255,0.40)]'
              : ''
          }`}
        >
          <StrategyCard title='Getting to Know You'>
            <span>
              {hasGettingToKnowYouContent
                ? 'Business details have been defined'
                : 'Share your business details'}
            </span>
          </StrategyCard>
        </div>

        <div
          onClick={() => handleCardClick('tone-of-voice')}
          className={`cursor-pointer transition-all duration-200 ${
            selectedSection === 'tone-of-voice'
              ? 'rounded-16 border border-[#0866FF] bg-white shadow-[2px_2px_8px_0px_rgba(8,102,255,0.40)]'
              : ''
          }`}
        >
          <StrategyCard title='Tone of Voice'>
            <span>
              {brandStrategy?.tone_of_voices?.length
                ? `${brandStrategy.tone_of_voices.length} tone of voices defined`
                : 'Define your tone of voice'}
            </span>
          </StrategyCard>
        </div>

        <div
          onClick={() => handleCardClick('content-plan')}
          className={`cursor-pointer transition-all duration-200 ${
            selectedSection === 'content-plan'
              ? 'rounded-16 border border-[#0866FF] bg-white shadow-[2px_2px_8px_0px_rgba(8,102,255,0.40)]'
              : ''
          }`}
        >
          <StrategyCard title='Content Plan'>
            <span>
              {brandStrategy?.objectives
                ? 'Content plan has been defined'
                : 'Define your content plan'}
            </span>
          </StrategyCard>
        </div>
      </div>
    </div>
  );
};

export default StrategyBlocks;
